import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { PropsWithChildren, ReactNode } from 'react';

import COLORS from '../../../../theme/colors';
import MetricFacet from '../../MetricFacet';

import styles from './DonutChart.module.less';

type ColorizedDatum = { name: string, value: number, fill: string };

type ColorizedData = ColorizedDatum[];

type UncolorizedDatum = {name: string, value: number};

type UncolorizedData = UncolorizedDatum[];

type Data = ColorizedData | UncolorizedData;

/**
 * Props for a StaticDonutChart.
 */
export type Props = PropsWithChildren<{
  /**
   * The label for the chart.
   */
  label: ReactNode;
  /**
   * The data for the chart.
   */
  data: Data;
  /**
   * The sum of the values of the whole domain.
   * Defaults to the sum of the values from data.
   */
  total?: number;
  /**
   * The size (outer diameter) of the chart.
   */
  size: number;
  /**
   * The value for the "other" category.
   * This is a numeric catchall that gets added as an extra fossaGray20 sector.
   * Defaults to the difference between the total and the sum of the data.
   */
  other?: number;
  /**
   * The background fill color for the chart.
   * Defaults to fossaGray02.
   */
  backgroundFill?: string;
  /**
   * The start angle of the chart.
   */
  startAngle?: number;
  /**
   * The end angle of the chart.
   */
  endAngle?: number;
  /**
   * The inner radius of the chart.
   * Defaults to size / 4.
   */
  innerRadius?: number;
  /**
   * The outer radius of the chart.
   * Defaults to size / 2.
   */
  outerRadius?: number;
  /**
   * The padding angle of the chart.
   */
  paddingAngle?: number;
  /**
   * The width of outlines of the chart's sectors.
   */
  strokeWidth?: number;
}>;

const StaticDonutChart = ({
  label,
  data,
  total,
  size,
  other,
  backgroundFill,
  startAngle,
  endAngle,
  innerRadius,
  outerRadius, paddingAngle,
  strokeWidth,
}: Props) => {
  // Colorize the given data, or determine that it is already colorized
  const isColorized = (rawData: Data): rawData is ColorizedData => rawData.every((datum) => datum.hasOwnProperty('fill'));

  const loopOneColors = [
    COLORS.fossaBlue, COLORS.fossaPurple, COLORS.fossaGreen, COLORS.fossaOrange,
    COLORS.fossaRed, COLORS.fossaPink, COLORS.fossaCyan, COLORS.fossaYellow,
  ];

  const loopTwoColors = [
    COLORS.fossaBlue60, COLORS.fossaPurple60, COLORS.fossaGreen60, COLORS.fossaOrange60,
    COLORS.fossaRed60, COLORS.fossaPink60, COLORS.fossaCyan60, COLORS.fossaYellow60,
  ];

  const defaultColors = [...loopOneColors, ...loopTwoColors];

  const colorizeData = (rawData: Data): ColorizedData => isColorized(rawData)
    ? rawData
    : rawData.map((datum, idx) => ({
      ...datum,
      fill: defaultColors[idx % defaultColors.length] as string,
    }));

  const colorizedData = colorizeData(data);
  const valueSum = data.reduce((acc, { value }) => acc + value, 0);
  const totalOrDefault = total ?? valueSum;
  const isEmptyChart = totalOrDefault === 0;
  const otherOrDefault = other ?? totalOrDefault - valueSum;
  const colorizedDataWithOther = isEmptyChart
    ? [{ name: 'Other', value: 1, fill: COLORS.fossaGray20 }]
    : [...colorizedData, { name: 'Other', value: otherOrDefault, fill: COLORS.fossaGray20 }];

  // The space between the pieces of the donut
  const backgroundFillOrDefault = backgroundFill || COLORS.fossaGray02;

  return (
    <MetricFacet>
      <MetricFacet.Total>{totalOrDefault}</MetricFacet.Total>
      <MetricFacet.Label>{label}</MetricFacet.Label>
      <MetricFacet.Body>
        <ResponsiveContainer width={size} height={size}>
          <PieChart>
            <Pie
              data={colorizedDataWithOther}
              innerRadius={innerRadius ?? (size / 4)}
              outerRadius={outerRadius ?? (size / 2)}
              fill={backgroundFillOrDefault}
              paddingAngle={paddingAngle ?? 0}
              dataKey="value"
              startAngle={startAngle ?? 90}
              endAngle={endAngle ?? -270}
            >
              {
                // The explicit Cell's stroke/strokeWidth
                // determine the space between the pieces of
                // the donut.
                colorizedDataWithOther.map(({ name }) => (
                  <Cell stroke={backgroundFillOrDefault} strokeWidth={strokeWidth ?? 2} key={name} />
                ))
              }
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </MetricFacet.Body>
      <MetricFacet.Legend>
        <div className={styles.legend}>
          {
            colorizedData.map(({ name, fill }) => (
              <span key={name} className={styles.entry}>
                <div className={styles.swatch} style={{ backgroundColor: fill }} />
                {name}
              </span>
            ))
          }
        </div>
      </MetricFacet.Legend>
    </MetricFacet>
  );
};

export default StaticDonutChart;
